import React, { useEffect } from "react";
import {
  IconCircleCheck,
  IconDatabase,
  IconTarget,
  IconBuilding,
  IconUser,
  IconClock,
  IconShieldCheck,
  IconLock,
  IconMail,
} from "@tabler/icons-react";
import "../policy.css";
import PolicyContact from "../PolicyContact";
import PolicyFooter from "../PolicyFooter";

const COMMITMENTS = [
  <>
    Data Anda <b>tidak akan dijual atau disewakan</b> kepada pihak ketiga
    manapun.
  </>,
  <>
    Data hanya digunakan untuk <b>tujuan yang Anda setujui</b> saat pendaftaran.
  </>,
  <>
    Anda dapat <b>meminta akses, perbaikan, atau penghapusan</b> data Anda kapan
    saja.
  </>,
  <>
    Sistem kami diaudit secara berkala sesuai standar <b>ISO 27001:2022</b>.
  </>,
];

const COLLECTED = [
  { cat: "Identitas", val: "Nama, sapaan, email, nomor WhatsApp, NIK/NPWP" },
  { cat: "Profesi", val: "Pekerjaan, jabatan, nama perusahaan" },
  {
    cat: "Transaksi",
    val: (
      <>
        Riwayat pendaftaran, metode pembayaran (token kartu disimpan di{" "}
        <i>payment gateway</i>, bukan di server kami)
      </>
    ),
  },
  {
    cat: "Teknis",
    val: <i>IP address, cookies untuk fungsi login &amp; analitik</i>,
  },
];

const PURPOSES = [
  {
    t: "Administrasi pendaftaran",
    d: "Pencatatan peserta, komunikasi schedule, sertifikat",
  },
  {
    t: "Faktur pajak & invoice",
    d: "Penerbitan dokumen sesuai regulasi perpajakan",
  },
  {
    t: "Komunikasi program",
    d: "Pengingat, perubahan jadwal, materi pelatihan",
  },
  {
    t: "Promosi (opsional)",
    d: "Info program lain, hanya jika Anda menyetujui terpisah. Bisa dicabut kapan saja.",
    amber: true,
  },
];

const THIRD_PARTIES = [
  {
    t: "Xendit",
    d: "Pemroses pembayaran (VA, kartu kredit, QRIS). Data kartu tidak disimpan di server prasmul-eli.",
  },
  {
    t: "Penyedia email",
    d: "Pengiriman invoice, link pembayaran, dan komunikasi program.",
  },
  {
    t: "Cloud hosting",
    d: "Penyimpanan data dengan enkripsi sesuai standar ISO 27001.",
  },
];

const RIGHTS = [
  { t: "Akses data", d: "Meminta salinan data Anda" },
  { t: "Perbaikan", d: "Memperbarui data yang tidak akurat" },
  { t: "Penghapusan", d: "Meminta data dihapus" },
  { t: "Tarik persetujuan", d: "Sewaktu-waktu" },
  { t: "Portabilitas", d: "Minta data dalam format yang dapat dipindahkan" },
  { t: "Keberatan", d: "Atas pemrosesan tertentu" },
];

const RETENTION = [
  { k: "Data identitas peserta (NIK/NPWP)", v: "5 tahun" },
  { k: "Data transaksi & faktur pajak", v: "10 tahun (sesuai UU Perpajakan)" },
  { k: "Data komunikasi & log", v: "2 tahun" },
  { k: "Data promosi (jika disetujui)", v: "Sampai dicabut" },
];

const SECURITY = [
  <>
    Enkripsi data <b>at rest (AES-256)</b> dan <b>in transit (TLS 1.3)</b>
  </>,
  <>Akses berbasis peran (role-based access control) — hanya tim yang relevan</>,
  <>Audit trail untuk setiap akses data sensitif</>,
  <>Audit ISO 27001 berkala oleh badan sertifikasi independen</>,
  <>Pelatihan keamanan rutin untuk seluruh karyawan</>,
  <>
    Prosedur respons insiden dalam <b>72 jam</b> sesuai mandat UU PDP
  </>,
];

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = "Kebijakan Privasi & Perlindungan Data | prasmul-eli";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      {/* Hero */}
      <header className="policy-hero">
        <div className="policy-hero-inner">
          <h1>Kebijakan Privasi &amp; Perlindungan Data</h1>
          <p className="policy-hero-sub">
            Data Anda aman di sini. Sesuai UU PDP No. 27/2022.
          </p>
          <div className="policy-hero-badges">
            <span className="policy-badge">
              <IconLock size={18} />
              <span>
                <span className="policy-badge-title">ISO 27001:2022</span>
                <br />
                <span className="policy-badge-sub">
                  Information Security Management
                </span>
              </span>
            </span>
            <span className="policy-badge">
              <span>
                <span className="policy-badge-title">UU PDP Compliant</span>
                <br />
                <span className="policy-badge-sub">UU No. 27 Tahun 2022</span>
              </span>
            </span>
          </div>
        </div>
      </header>

      <main className="policy-body">
        {/* Komitmen */}
        <section className="policy-card">
          <div className="policy-card-head bordered">
            <span className="policy-card-icon pi-green">
              <IconCircleCheck size={22} />
            </span>
            <div>
              <h2 className="policy-card-title">Komitmen kami dalam 1 menit</h2>
            </div>
          </div>
          <div className="policy-check-list">
            {COMMITMENTS.map((c, i) => (
              <div className="policy-check" key={i}>
                <IconCircleCheck size={18} />
                <span>{c}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Data yang dikumpulkan */}
        <section className="policy-card">
          <div className="policy-card-head">
            <span className="policy-card-icon pi-blue">
              <IconDatabase size={22} />
            </span>
            <div>
              <h2 className="policy-card-title">Data yang kami kumpulkan</h2>
            </div>
          </div>
          <div className="policy-datatable">
            <div className="pdt-head">
              <span className="pdt-col-cat">Kategori</span>
              <span className="pdt-col-val">Jenis Data</span>
            </div>
            {COLLECTED.map((r, i) => (
              <div className="pdt-row" key={i}>
                <span className="pdt-col-cat">{r.cat}</span>
                <span className="pdt-col-val">{r.val}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Tujuan penggunaan */}
        <section className="policy-card">
          <div className="policy-card-head">
            <span className="policy-card-icon pi-blue">
              <IconTarget size={22} />
            </span>
            <div>
              <h2 className="policy-card-title">Tujuan penggunaan data</h2>
            </div>
          </div>
          <div className="policy-numlist">
            {PURPOSES.map((p, i) => (
              <div className={`policy-numrow${p.amber ? " amber" : ""}`} key={i}>
                <span className="num-badge">{i + 1}</span>
                <div>
                  <p className="numrow-title">{p.t}</p>
                  <p className="numrow-text">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pihak ketiga */}
        <section className="policy-card">
          <div className="policy-card-head bordered">
            <span className="policy-card-icon pi-gray">
              <IconBuilding size={22} />
            </span>
            <div>
              <h2 className="policy-card-title">
                Pihak ketiga yang memproses data Anda
              </h2>
              <p className="policy-card-subtitle">
                Terikat perjanjian kerahasiaan &amp; kewajiban perlindungan data
              </p>
            </div>
          </div>
          <div style={{ height: 12 }} />
          {THIRD_PARTIES.map((p, i) => (
            <div className="policy-stackbox" key={i}>
              <p className="t">{p.t}</p>
              <p className="d">{p.d}</p>
            </div>
          ))}
          <div style={{ height: 12 }} />
        </section>

        {/* Hak Anda */}
        <section className="policy-card">
          <div className="policy-card-head bordered">
            <span className="policy-card-icon pi-violet">
              <IconUser size={22} />
            </span>
            <div>
              <h2 className="policy-card-title">Hak Anda sebagai pemilik data</h2>
              <p className="policy-card-subtitle">Sesuai Pasal 5-14 UU PDP</p>
            </div>
          </div>
          <div style={{ height: 12 }} />
          <div className="policy-rights-grid">
            {RIGHTS.map((r, i) => (
              <div className="policy-right-card" key={i}>
                <p className="t">{r.t}</p>
                <p className="d">{r.d}</p>
              </div>
            ))}
          </div>
          <div className="policy-info-strip">
            <IconMail size={18} />
            <span>
              Untuk menjalankan hak ini: kirim email ke [email DPO] atau ajukan
              via dashboard akun. Permintaan diproses dalam <b>3×24 jam kerja</b>.
            </span>
          </div>
        </section>

        {/* Retensi data */}
        <section className="policy-card">
          <div className="policy-card-head">
            <span className="policy-card-icon pi-green">
              <IconClock size={22} />
            </span>
            <div>
              <h2 className="policy-card-title">Berapa lama data disimpan</h2>
            </div>
          </div>
          {RETENTION.map((r, i) => (
            <div className="policy-retain-row" key={i}>
              <span className="k">{r.k}</span>
              <span className="v">{r.v}</span>
            </div>
          ))}
          <p className="policy-italic" style={{ paddingTop: 14 }}>
            Setelah masa simpan berakhir, data dimusnahkan secara permanen
            menggunakan prosedur sesuai ISO 27001.
          </p>
        </section>

        {/* Keamanan teknis */}
        <section className="policy-card">
          <div className="policy-card-head bordered">
            <span className="policy-card-icon pi-green">
              <IconShieldCheck size={22} />
            </span>
            <div>
              <h2 className="policy-card-title">Keamanan teknis &amp; organisasi</h2>
            </div>
          </div>
          <div style={{ height: 12 }} />
          <div className="policy-sec-grid">
            {SECURITY.map((s, i) => (
              <div className="policy-sec-item" key={i}>
                <span className="b-dot" />
                <span>{s}</span>
              </div>
            ))}
          </div>
        </section>

        <PolicyContact />
      </main>

      <PolicyFooter active="privacy" />
    </div>
  );
};

export default PrivacyPolicyPage;
